import dayjs from "single-spa/dayjs-extend";

// Industry datasets were backfilled at different points in time, so each endpoint
// has its own earliest supported start date. Keeping every boundary here (rather
// than inline per page/widget) is what prevents the pages and the Overview
// widgets from drifting apart.
export const IndustryDataAvailability = {
    DECEMBER_2024: "2024-12-01",
    MAY_2025: "2025-05-01",
} as const;

export type IndustryDataAvailabilityDate = (typeof IndustryDataAvailability)[keyof typeof IndustryDataAvailability];

// The `pro-api-client` method name of every industry endpoint that has a minimum
// available date. Callers reference their own endpoint by name so the boundary
// can never be attached to the wrong request.
export const INDUSTRY_ENDPOINT_MIN_DATE = {
    adintelligenceIndustryRecentAdsList: IndustryDataAvailability.DECEMBER_2024,
    adintelligenceIndustryTopCampaignsList: IndustryDataAvailability.DECEMBER_2024,
    adintelligenceIndustryAdPositionList: IndustryDataAvailability.DECEMBER_2024,
    adintelligenceIndustryActivityList: IndustryDataAvailability.MAY_2025,
    adintelligenceIndustryPerformanceOverTimeList: IndustryDataAvailability.MAY_2025,
    adintelligenceIndustryTopDomainsList: IndustryDataAvailability.MAY_2025,
    adintelligenceIndustryTopPublishersPreviewList: IndustryDataAvailability.MAY_2025,
    adintelligenceIndustryTopPublishersList: IndustryDataAvailability.MAY_2025,
    adintelligenceIndustryDeviceList: IndustryDataAvailability.MAY_2025,
    adintelligenceIndustryFormatList: IndustryDataAvailability.MAY_2025,
    adintelligenceIndustrySpendRankList: IndustryDataAvailability.MAY_2025,
} as const satisfies Record<string, IndustryDataAvailabilityDate>;

export const INDUSTRY_DATA_AVAILABILITY_BANNER_KEY: Record<IndustryDataAvailabilityDate, string> = {
    [IndustryDataAvailability.DECEMBER_2024]: "ad.intelligence.industry.data.availability.december.2024.banner.message",
    [IndustryDataAvailability.MAY_2025]: "ad.intelligence.industry.data.availability.may.2025.banner.message",
};

interface IResolveDataAvailabilityParams {
    /** Selected range start, `yyyy-MM-dd` (the endpoint's `from`). */
    from: string;
    /** Selected range end, `yyyy-MM-dd` (the endpoint's `to`). */
    to: string;
    minDate: IndustryDataAvailabilityDate;
}

export interface IIndustryDataAvailability {
    /** `from` clamped to `minDate`; a later user-selected start is preserved. */
    from: string;
    /** Range starts before `minDate` but still overlaps the supported window. */
    isPartiallyAvailable: boolean;
    /** Range ends before `minDate` — no request should be sent at all. */
    isUnavailable: boolean;
    /** Range starts before `minDate` (partially available OR fully unavailable). */
    isBeforeMinDate: boolean;
}

/** What a page/widget needs to render its availability notice and empty state. */
export interface IIndustryDataAvailabilityState {
    minDate: IndustryDataAvailabilityDate;
    /** Show the availability notice. */
    isBeforeMinDate: boolean;
    /** Request was skipped — the page/widget must fall back to its empty state. */
    isFullyUnavailable: boolean;
}

/**
 * Resolves a selected date range against an endpoint's earliest supported date.
 *
 * - range entirely before `minDate` -> `isUnavailable` (skip the request)
 * - range overlapping `minDate`     -> `from` clamped to `minDate`
 * - range entirely on/after         -> untouched
 */
export const resolveDataAvailability = ({
    from,
    to,
    minDate,
}: IResolveDataAvailabilityParams): IIndustryDataAvailability => {
    const min = dayjs.utc(minDate);
    const isBeforeMinDate = dayjs.utc(from).isBefore(min, "day");
    const isUnavailable = dayjs.utc(to).isBefore(min, "day");

    return {
        from: isBeforeMinDate ? minDate : from,
        isPartiallyAvailable: isBeforeMinDate && !isUnavailable,
        isUnavailable,
        isBeforeMinDate,
    };
};
